import { useEffect, useRef, useState, type RefObject } from "react"
import type { AgGridReact } from "ag-grid-react"

/**
 * The rowData to hand the grid. Same rows in the same order (an edit, not a regrouping) go in as
 * an update transaction of only the changed rows: a new rowData would make AG Grid diff every row.
 */
export function useIncrementalRowData<R extends { id: string }>(
  gridRef: RefObject<AgGridReact<R> | null>,
  rows: R[],
): R[] {
  const [rowData, setRowData] = useState(rows)
  const rowsInGrid = useRef<R[]>([])
  useEffect(() => {
    const api = gridRef.current?.api
    const held = rowsInGrid.current
    rowsInGrid.current = rows
    const sameRows =
      held.length === rows.length && held.every((row, index) => row.id === rows[index]?.id)
    if (!api || !sameRows) {
      setRowData(rows)
      return
    }
    const update = rows.filter((row, index) => row !== held[index])
    if (update.length > 0) api.applyTransaction({ update })
  }, [gridRef, rows])
  return rowData
}
